import { argbFromHex, blueFromArgb, greenFromArgb, redFromArgb } from '@material/material-color-utilities'

import { getCssVariableValue, MtColorScheme } from '../../brand-kit'

export interface SectionBackgroundGroupProps {
  id: number
  type: string
  values: Partial<SectionBackgroundGroupValues> | Record<string, Partial<SectionBackgroundGroupValues>>
}

export interface SectionColorVariant {
  light: string | number | number[]
  dark: string | number | number[]
}

export interface SectionBackgroundGroupValues {
  enabled: boolean
  color: string | SectionColorVariant
  colorOpacity: SectionColorVariant
  opacity: number
  url: string
  loop: boolean
  autoplay: boolean
  startTime: number
  type: string
  parallax: boolean
  muted: boolean
  mode: {
    light: string
    dark: string
  }
  direction: string
  gradientStart: SectionColorVariant
  gradientStartOpacity: SectionColorVariant
  gradientEnd: SectionColorVariant
  gradientEndOpacity: SectionColorVariant
  bg: string
  fg: string
  size: number
  xpos: number
  ypos: number
  rotate: number
  inverted: boolean
  animationType?: 'none' | 'parallax' | 'ken-burns' | 'fade' | 'zoom' | 'pan'
  animationSpeed?: number
  animationDuration?: number
  // Effect/Animation background properties
  effectType?:
    | 'none'
    | 'gradient'
    | 'animated-gradient'
    | 'beams'
    | 'wavy'
    | 'sparkles'
    | 'flicker-grid'
    | 'meteors'
    | 'stars'
    | 'boxes'
    | 'aurora'
    | 'paths'
    | 'fluid-gradient'
    | 'shader-clouds'
    | 'orbs'
    | 'dot-matrix'
    | 'spotlight'
    | 'noise'
    | 'mesh-gradient'
    | 'ripple-waves'
    | 'floating-bubbles'
    | 'neural-network'
    | 'matrix-rain'
    | 'particles-flow'
  effectSpeed?: number // 0-200, default 100
  effectOpacity?: number // 0-100, default 100
  effectColors?: string[] // Array of hex colors
  effectBackgroundColor?: string // Background color for effect
}

export const DEFAULT_MASK_VALUES = {
  type: '#',
  opacity: 0.05,
  inverted: false,
  rotate: 0,
  size: 150,
  xpos: 50,
  ypos: 50,
}

export const DEFAULT_SECTION_BACKGROUND_GROUP: SectionBackgroundGroupProps[] = [
  {
    id: 6,
    type: 'overlay',
    values: {
      enabled: false,
      color: '#000000',
      opacity: 0.5,
    },
  },
  {
    id: 7,
    type: 'effect',
    values: {
      effectType: 'none',
      effectSpeed: 100,
      effectOpacity: 100,
      effectColors: ['#6366f1', '#8b5cf6', '#ec4899'],
      effectBackgroundColor: '#000000',
    },
  },
  {
    id: 5,
    type: 'mask',
    values: {
      phone: DEFAULT_MASK_VALUES,
      'phone-landscape': DEFAULT_MASK_VALUES,
      tablet: DEFAULT_MASK_VALUES,
      'tablet-landscape': DEFAULT_MASK_VALUES,
      desktop: DEFAULT_MASK_VALUES,
      tv: DEFAULT_MASK_VALUES,
    },
  },
  {
    id: 4,
    type: 'video',
    values: {
      url: '',
      loop: true,
      muted: true,
    },
  },
  {
    id: 3,
    type: 'image',
    values: {
      url: '',
      parallax: false,
      // animationType: 'none',
      // animationSpeed: 0.5,
      // animationDuration: 20,
    },
  },
  {
    id: 2,
    type: 'pattern',
    values: {
      type: '',
      opacity: 0.1,
      color: 'primary-10',
    },
  },
  {
    id: 1,
    type: 'color',
    values: {
      mode: {
        light: 'Solid Color',
        dark: 'Solid Color',
      },
      color: {
        light: 'primary-80',
        dark: 'primary-100',
      },
      colorOpacity: {
        light: 100,
        dark: 100,
      },
      gradientStart: {
        light: 'primary-70',
        dark: 'primary-30',
      },
      gradientStartOpacity: {
        light: 1,
        dark: 1,
      },
      gradientEnd: {
        light: 'primary-80',
        dark: 'primary-40',
      },
      gradientEndOpacity: {
        light: 1,
        dark: 1,
      },
    },
  },
]

interface PatternStyle {
  background: string
  opacity: number
  backgroundSize?: string
  backgroundRepeat?: string
  backgroundColor?: string
}

export const createPatternStyle = (
  color: string,
  size: number,
  opacity: number,
  bgPageColor: string
): Record<string, PatternStyle> => {
  const generateOpColor = (op = '80') => getCssVariableValue(color ?? `var(--color-primary-10)`) + op

  return {
    Zigzag: {
      background: `linear-gradient(45deg, currentColor 25%, transparent 25%) 0% 0% / ${size}px ${size}px, linear-gradient(315deg, currentColor 25%, transparent 25%) 0% 0% / ${size}px ${size}px,linear-gradient(45deg, transparent 24%,${color} 25%, ${color} 45%, transparent 45%) 0% 0% / ${size}px ${size}px, linear-gradient(315deg, transparent 24%,${color} 25%, ${color} 45%, transparent 45%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Wave: {
      background: `radial-gradient(circle at top left,transparent 25%,${color} 25.5%, ${color} 36%, transparent 37%, transparent 100%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at bottom right,transparent 34%,${color} 34.5%, ${color} 45.5%, transparent 46%, transparent 100%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Dot1: {
      // Ddot1
      background: `radial-gradient(circle, ${color} 10%, transparent 11%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Dot2: {
      // Dot 7
      background: `radial-gradient(circle at top right,transparent 10%,${color} 10%, ${color} 20%, transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left bottom,transparent 10%,${color} 10%,${color} 20%, transparent 21%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at top left,transparent 10%,${color} 10%,${color} 20%, transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right bottom,transparent 10%,${color} 10%,${color} 20%, transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at center,${color} 30%, transparent 31%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Dot3: {
      // Dot 8
      background: `radial-gradient(circle at 25% 25%, ${color} 20%, transparent 21%, transparent 100%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at 75% 75%, ${color} 15%, transparent 16%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Dot4: {
      // Dot 9
      background: `radial-gradient(${color} 20%, transparent 21% ,transparent 79%, ${color} 80%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle1: {
      // Circle 2
      background: `radial-gradient(transparent 24%, ${color} 25%, ${color} 35%, transparent 36%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle2: {
      // Circle 5
      background: `radial-gradient(circle at top,transparent 14%, ${color} 15% ,${color} 19% , transparent 20%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom,transparent 14%, ${color} 15% ,${color} 19% , transparent 20%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right,transparent 14%, ${color} 15% ,${color} 19% , transparent 20%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left,transparent 14%, ${color} 15% ,${color} 19% , transparent 20%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle3: {
      // Circle 7
      background: `radial-gradient(${color} 10%, transparent 10%, transparent 39%, ${color} 40%,${color} 45%, transparent 46%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle4: {
      // Circle 9
      background: `radial-gradient(circle at 25% 25%,transparent 14%,${color} 15%, ${color} 20%, transparent 21%, transparent 100%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at 75% 75%, transparent 11%, ${color} 12%, ${color} 17%, transparent 18%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle5: {
      // Circle 14
      background: `radial-gradient(${color} 15%, transparent 16%) 0% 0% / ${size}px ${size}px, linear-gradient(45deg, transparent 49%, ${color} 49% 51%, transparent 51%) 0% 0% / ${size}px ${size}px, linear-gradient(-45deg, transparent 49%, ${color} 49% 51%, transparent 51%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle6: {
      // Circle 18
      background: `radial-gradient(circle at 40% 40%, ${color} 25%, transparent 26%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at 60% 60%, ${generateOpColor('80')} 25%, transparent 26%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle7: {
      // Cirlce 20
      background: `radial-gradient(transparent 34%, ${color} 35%, ${color} 45%, transparent 46%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left, transparent 39%, ${generateOpColor('80')} 40%, ${generateOpColor('80')} 45%, transparent 46%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right, transparent 39%, ${generateOpColor('80')} 40%, ${generateOpColor('80')} 45%, transparent 46%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle8: {
      // Circle 21
      background: `radial-gradient(circle at center bottom, ${color} 5%,transparent 6%, transparent 19%, ${color} 20%, ${color} 25%, transparent 26%, transparent 39%, ${color} 40%, ${color} 45%, transparent 46%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at center top, ${color} 5%,transparent 6%, transparent 19%, ${color} 20%, ${color} 25%, transparent 26%,transparent 39%, ${color} 40%, ${color} 45%, transparent 46%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left,${color} 5%, transparent 6%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right,${color} 5%, transparent 6%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle9: {
      //Circle 22
      background: `radial-gradient(${color} 15%, transparent 16%, transparent 49%, ${color} 50%, transparent 51%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at top left, ${color} 10%, transparent 10%, transparent 39%, ${color} 40%, transparent 41%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at top right, ${color} 10%, transparent 10%, transparent 39%, ${color} 40%, transparent 41%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at bottom left, ${color} 10%, transparent 10%, transparent 39%, ${color} 40%, transparent 41%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at bottom right, ${color} 10%, transparent 10%, transparent 39%, ${color} 40%, transparent 41%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle10: {
      //Circle 25
      background: `radial-gradient(circle at top,transparent 60%, ${color} 61%,${color} 66%, transparent 67%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at bottom,transparent 60%, ${color} 61%,${color} 66%, transparent 67%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at left,transparent 60%, ${color} 61%,${color} 66%, transparent 67%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at right,transparent 60%, ${color} 61%,${color} 66%, transparent 67%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle11: {
      //Circle 26
      background: `radial-gradient(circle at top left, transparent 14%,${color} 15%,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at top right, transparent 14%,${color} 15%,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at bottom left, transparent 14%,${color} 15%,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at bottom right, transparent 14%,${color} 15%,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at top,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Circle12: {
      //Circle 27
      background: `radial-gradient(circle at bottom left ,${color} 35%, transparent 36%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top right ,${color} 35%, transparent 36%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diagonal1: {
      //Diagonal 1
      background: `linear-gradient(45deg, ${color} 0%, ${color} 5%,transparent 5%, transparent 10%, ${color} 10%, ${color} 15%,transparent 15%,transparent 20%, ${color} 20%, ${color} 25%,transparent 25%,transparent 30%, ${color} 30%, ${color} 35%,transparent 35%,transparent 40%, ${color} 40%, ${color} 45%,transparent 45%,transparent 50%, ${color} 50%, ${color} 55%,transparent 55%,transparent 60%, ${color} 60%, ${color} 65%,transparent 65%,transparent 70%, ${color} 70%, ${color} 75%,transparent 70%,transparent 80%, ${color} 80%, ${color} 85%,transparent 85%,transparent 90%, ${color} 90%, ${color} 95%,transparent 95%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diagonal2: {
      //Diagonal 5
      background: `repeating-linear-gradient(45deg, ${color} 0, ${color} 10%, transparent 0, transparent 50%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diagonal3: {
      //Diagonal 6
      background: `repeating-linear-gradient(135deg, ${color} 0, ${color} 10%, transparent 0, transparent 50%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diagonal4: {
      //Diagonal 7
      background: `linear-gradient(135deg, ${bgPageColor} 20%, transparent 20%, transparent 80%, ${bgPageColor} 80%) 0% 0% / ${size}px ${size}px,linear-gradient(45deg, ${bgPageColor} 40%, ${color} 40%, ${color} 60%, ${color} 60%, ${bgPageColor} 60%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, ${bgPageColor} 20%, ${color} 20%, ${color} 80%, ${bgPageColor} 80%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diagonal5: {
      //Diagonal 8
      background: `linear-gradient(45deg, ${bgPageColor} 20%, transparent 20%, transparent 80%, ${bgPageColor} 80%) 0% 0% / ${size}px ${size}px,linear-gradient(135deg, ${bgPageColor} 40%, ${color} 40%, ${color} 60%, ${color} 60%, ${bgPageColor} 60%) 0% 0% / ${size}px ${size}px, linear-gradient(45deg, ${bgPageColor} 20%, ${color} 20%, ${color} 80%, ${bgPageColor} 80%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Triangle1: {
      //Triangle 1
      background: `linear-gradient(45deg,${color} 10%, transparent 10%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, transparent 90%, ${color} 90%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Triangle2: {
      //Triangle 5
      background: `linear-gradient(135deg, ${color} 50%, transparent 50%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Triangle3: {
      //Triangle 7
      background: `linear-gradient(315deg, ${color} 50%, transparent 50%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Triangle4: {
      //Triangle 8
      background: `linear-gradient(45deg, ${color} 25%, transparent 26%, transparent 74%, ${color} 75%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Triangle5: {
      //Triangle 11
      background: `linear-gradient(0deg, ${bgPageColor} 25%, transparent 26%) 0% 0% / ${size}px ${size}px, linear-gradient(90deg, ${bgPageColor} 25%, transparent 26%) 0% 0% / ${size}px ${size}px, linear-gradient(180deg, ${color} 25%, transparent 26%) 0% 0% / ${size}px ${size}px, linear-gradient(270deg, ${color} 25%, transparent 26%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Triangle6: {
      //Triangle 15
      background: `linear-gradient(135deg, ${generateOpColor('80')} 50%, transparent 0) 0% 0% / ${size}px ${size}px,linear-gradient(-135deg, ${generateOpColor('80')} 50%, transparent 0) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Triangle7: {
      //Triangle 16
      background: `linear-gradient(45deg, ${color} 0%, ${color} 5%,transparent 5%, transparent 10%, ${color} 10%, ${color} 15%,transparent 15%,transparent 20%, ${color} 20%, ${color} 25%,transparent 25%,transparent 30%, ${color} 30%, ${color} 35%,transparent 35%,transparent 40%, ${color} 40%, ${color} 45%,transparent 45%,transparent 50%, ${color} 50%, ${color} 100%,transparent 55%,transparent 60%, ${color} 60%, ${color} 65%,transparent 65%,transparent 70%, ${color} 70%, ${color} 75%,transparent 70%,transparent 80%, ${color} 80%, ${color} 85%,transparent 85%,transparent 90%, ${color} 90%, ${color} 95%,transparent 95%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, ${color} 0%, ${color} 5%,transparent 5%, transparent 10%, ${color} 10%, ${color} 15%,transparent 15%,transparent 20%, ${color} 20%, ${color} 25%,transparent 25%,transparent 30%, ${color} 30%, ${color} 35%,transparent 35%,transparent 40%, ${color} 40%, ${color} 45%,transparent 45%,transparent 50%, ${color} 50%, ${color} 55%,transparent 55%,transparent 60%, ${color} 60%, ${color} 65%,transparent 65%,transparent 70%, ${color} 70%, ${color} 75%,transparent 70%,transparent 80%, ${color} 80%, ${color} 85%,transparent 85%,transparent 90%, ${color} 90%, ${color} 95%,transparent 95%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line1: {
      //Line 1
      background: `linear-gradient(90deg, transparent 45%, ${color} 45%, ${color} 55%, transparent 55%, transparent 20%, ${color} 20%, ${color} 30%,transparent 30%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line2: {
      //Line 2
      background: `linear-gradient(0deg, transparent 45%, ${color} 45%, ${color} 55%, transparent 55%, transparent 20%, ${color} 20%, ${color} 30%,transparent 30%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line3: {
      //Line 3
      background: `repeating-linear-gradient(90deg, ${color} 0, ${color} 5%, transparent 0, transparent 50%) 0% 0% / ${size}px ${size}px, repeating-linear-gradient(180deg, ${color} 0, ${color} 5%, transparent 0, transparent 50%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line4: {
      //Line 4
      background: `linear-gradient(-71deg, ${color} 25%, transparent 26%, transparent 74%, ${color} 75%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line5: {
      //Line 5
      background: `linear-gradient(19deg, ${color} 25%, transparent 26%, transparent 74%, ${color} 75%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line6: {
      //Line 6
      background: `linear-gradient(90deg, transparent 24%,${color} 25%, ${color} 30%, transparent 31%, transparent 39%,${color} 40%, ${color} 45%, transparent 45%) 0% 0% / ${size}px ${size}px,linear-gradient(180deg, transparent 24%,${color} 25%, ${color} 30%, transparent 31%, transparent 39%,${color} 40%, ${color} 45%, transparent 45%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line7: {
      //Line 7
      background: `linear-gradient(0deg, ${bgPageColor} 50%, transparent 21%) 0% 0% / ${size}px ${size}px, linear-gradient(90deg, transparent 45%, ${color} 45%, ${color} 55%, transparent 55%, transparent 20%, ${color} 20%, ${color} 30%,transparent 30%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line8: {
      //Line 8
      background: `linear-gradient(25deg, ${bgPageColor} 40%, transparent 41%, transparent 59%, ${bgPageColor} 60%) 0% 0% / ${size}px ${size}px, linear-gradient(90deg, transparent 45%, ${color} 45%, ${color} 55%, transparent 55%, transparent 20%, ${color} 20%, ${color} 30%,transparent 30%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line9: {
      //Line 10
      background: `linear-gradient(0deg, transparent 45%, ${color} 45%, ${color} 55%, transparent 55%, transparent 20%, ${color} 20%, ${color} 30%,transparent 30%) 0% 0% / ${size}px ${size}px, linear-gradient(90deg, transparent 45%, ${color} 45%, ${color} 55%, transparent 55%, transparent 20%, ${color} 20%, ${color} 30%,transparent 30%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Line10: {
      //Line 11
      background: `linear-gradient(45deg,transparent 34%, ${color} 35%, ${color} 40%, transparent 41%, transparent 59%, ${color} 60%, ${color} 65%, transparent 66%) 0% 0% / ${size}px ${size}px,linear-gradient(135deg,transparent 34%, ${color} 35%, ${color} 40%, transparent 41%, transparent 59%, ${color} 60%, ${color} 65%, transparent 66%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Symbol1: {
      //Symbol 1
      background: `radial-gradient(circle, transparent 25%, ${bgPageColor} 26%) 0% 0% / ${size}px ${size}px, linear-gradient(45deg, transparent 46%, ${color} 47%, ${color} 52%, transparent 53%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, transparent 46%, ${color} 47%, ${color} 52%, transparent 53%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Symbol2: {
      //Symbol 2
      background: `radial-gradient(circle, transparent 25%, ${bgPageColor} 26%) 0% 0% / ${size}px ${size}px, linear-gradient(0deg, transparent 44%, ${color} 45%, ${color} 55%, transparent 56%) 0% 0% / ${size}px ${size}px, linear-gradient(90deg, transparent 44%, ${color} 45%, ${color} 55%, transparent 56%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Stripe1: {
      //Stripe 1
      background: `linear-gradient(90deg,transparent 50%,${color} 50%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Stripe2: {
      //Stripe 2
      background: `linear-gradient(0deg,transparent 50%,${color} 50%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Stripe3: {
      //Stripe 3
      background: `linear-gradient(0deg,transparent 20%, ${color} 20%, ${color} 40%, transparent 40%, transparent 60%, ${color} 60%, ${color} 80%,transparent 80%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Stripe4: {
      //Stripe 4
      background: `linear-gradient(90deg,transparent 20%, ${color} 20%, ${color} 40%, transparent 40%, transparent 60%, ${color} 60%, ${color} 80%,transparent 80%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Stripe5: {
      //Stripe 5
      background: `linear-gradient(90deg,${color} 25%, transparent 26%, transparent 49%, ${generateOpColor('80')} 50%, ${generateOpColor('80')} 75%, transparent 76% ) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Stripe6: {
      //Stripe 6
      background: `linear-gradient(0deg,${color} 25%, transparent 26%, transparent 49%, ${generateOpColor('80')} 50%, ${generateOpColor('80')} 75%, transparent 76% ) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Cross1: {
      //Cross 1
      background: `linear-gradient(90deg, transparent 50%, ${color} 50%) 0% 0% / ${size}px ${size}px,linear-gradient(to bottom, transparent 50%, ${generateOpColor('80')} 50%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Cross2: {
      //Cross 2
      background: `repeating-linear-gradient(45deg, ${color} 0, ${color} 10%, transparent 0, transparent 50%) 0% 0% / ${size}px ${size}px, repeating-linear-gradient(135deg, ${color} 0, ${color} 10%, transparent 0, transparent 50%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Cross3: {
      //Cross 3
      background: `linear-gradient(45deg, transparent 49%, ${color} 49% 51%, transparent 51%) 0% 0% / ${size}px ${size}px, linear-gradient(-45deg, transparent 49%, ${color} 49% 51%, transparent 51%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Cross4: {
      //Cross 4
      background: `linear-gradient(45deg, ${color} 10%,transparent 10%,transparent 90%,${color} 90%) 0% 0% / ${size}px ${size}px,linear-gradient(135deg, transparent 40%, ${generateOpColor('80')} 40%, ${generateOpColor('80')} 60%, transparent 0) 0% 0% / ${size}px ${size}px,linear-gradient(45deg, transparent 40%, ${color} 40%, ${color} 60%, transparent 0) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diamond1: {
      //Diamond 1
      background: `linear-gradient(45deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diamond2: {
      //Diamond 2
      background: `radial-gradient(transparent 79%, ${color} 80%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diamond3: {
      // Diamond 3
      background: `linear-gradient(45deg, ${color} 0%, ${color} 5%,transparent 5%, transparent 10%, ${color} 10%, ${color} 15%,transparent 15%,transparent 20%, ${color} 20%, ${color} 25%,transparent 25%,transparent 30%, ${color} 30%, ${color} 35%,transparent 35%,transparent 40%, ${color} 40%, ${color} 45%,transparent 45%,transparent 50%, ${color} 50%, ${color} 55%,transparent 55%,transparent 60%, ${color} 60%, ${color} 65%,transparent 65%,transparent 70%, ${color} 70%, ${color} 75%,transparent 70%,transparent 80%, ${color} 80%, ${color} 85%,transparent 85%,transparent 90%, ${color} 90%, ${color} 95%,transparent 95%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, ${color} 0%, ${color} 5%,transparent 5%, transparent 10%, ${color} 10%, ${color} 15%,transparent 15%,transparent 20%, ${color} 20%, ${color} 25%,transparent 25%,transparent 30%, ${color} 30%, ${color} 35%,transparent 35%,transparent 40%, ${color} 40%, ${color} 45%,transparent 45%,transparent 50%, ${color} 50%, ${color} 55%,transparent 55%,transparent 60%, ${color} 60%, ${color} 65%,transparent 65%,transparent 70%, ${color} 70%, ${color} 75%,transparent 70%,transparent 80%, ${color} 80%, ${color} 85%,transparent 85%,transparent 90%, ${color} 90%, ${color} 95%,transparent 95%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diamond4: {
      // Diamond 4
      background: `linear-gradient(135deg, ${color} 25%, transparent 25%) 0% 0% / ${size}px ${size}px,linear-gradient(225deg, ${color} 25%, transparent 25%) 0% 0% / ${size}px ${size}px,linear-gradient(315deg, ${color} 25%, transparent 25%) 0% 0% / ${size}px ${size}px,linear-gradient(45deg, ${color} 25%, transparent 25%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diamond5: {
      // Diamond 5
      background: `linear-gradient(45deg, ${generateOpColor('80')} 25%, transparent 21%) 0% 0% / ${size}px ${size}px, linear-gradient(315deg, ${generateOpColor('80')} 25%, transparent 21%) 0% 0% / ${size}px ${size}px, linear-gradient(45deg, ${color} 10%, transparent 11%) 0% 0% / ${size}px ${size}px, linear-gradient(315deg, ${color} 10%, transparent 11%) 0% 0% / ${size}px ${size}px,linear-gradient(135deg, ${generateOpColor('80')} 25%, transparent 21%) 0% 0% / ${size}px ${size}px, linear-gradient(225deg, ${generateOpColor('80')} 25%, transparent 21%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, ${color} 10%, transparent 11%) 0% 0% / ${size}px ${size}px, linear-gradient(225deg, ${color} 10%, transparent 11%) 0% 0% / ${size}px ${size}px, radial-gradient(${color} 20%, transparent 21%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diamond6: {
      // Diamond 6
      background: `linear-gradient(-45deg, ${generateOpColor('99')} 20%, transparent 20% 80%, ${generateOpColor('99')} 80% 100%) 0% 0% / ${size}px ${size}px, linear-gradient(45deg, ${generateOpColor('99')} 20%, transparent 20% 80%, ${generateOpColor('99')} 80% 100%) 0% 0% / ${size}px ${size}px, linear-gradient(-45deg, ${color} 33%, transparent 33% 66%, ${color} 66%) 0% 0% / ${size}px ${size}px, linear-gradient(45deg, transparent 33%, ${color} 33% 66%, transparent 66%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diamond7: {
      // Diamond 7
      background: `linear-gradient(45deg,transparent 14%, ${color} 15%, ${color} 20%, transparent 21%, transparent 79%, ${color} 80%, ${color} 85%, transparent 86%) 0% 0% / ${size}px ${size}px,linear-gradient(135deg,transparent 14%, ${color} 15%, ${color} 20%, transparent 21%, transparent 79%, ${color} 80%, ${color} 85%, transparent 86%) 0% 0% / ${size}px ${size}px, radial-gradient(transparent 14%, ${color} 15%, ${color} 25%, transparent 26%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diamond8: {
      // Diamond 8
      background: `linear-gradient(45deg, ${generateOpColor('80')} 20%, transparent 21%, transparent 79%, ${color} 80%, ${color} 90%, transparent 91%) 0% 0% / ${size}px ${size}px, linear-gradient(315deg, ${generateOpColor('80')} 20%, transparent 21%) 0% 0% / ${size}px ${size}px, linear-gradient(45deg, ${color} 20%, transparent 21%) 0% 0% / ${size}px ${size}px, linear-gradient(315deg, ${color} 20%, transparent 21%, transparent 79%, ${color} 80%, ${color} 90%, transparent 91%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Diamond9: {
      // Diamond 9
      background: `linear-gradient(45deg, ${generateOpColor('80')} 20%, transparent 21%, transparent 79%, ${generateOpColor('80')} 80%) 0% 0% / ${size}px ${size}px, linear-gradient(315deg, ${generateOpColor('80')} 20%, transparent 21%) 0% 0% / ${size}px ${size}px, linear-gradient(45deg, ${color} 20%, transparent 21%) 0% 0% / ${size}px ${size}px, linear-gradient(315deg, ${color} 20%, transparent 21%, transparent 79%, ${generateOpColor('80')} 80%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed1: {
      background: `linear-gradient(45deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed2: {
      background: `
      linear-gradient(45deg, transparent 10%, ${color} 10% ,${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 90%, transparent 90%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, transparent 10%, ${color} 10% ,${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 90%, transparent 90%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left,${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed3: {
      background: `linear-gradient(45deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom,transparent 9%, ${color} 10% ,${color} 15%, transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(${color} 25%, transparent 26%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed4: {
      background: `linear-gradient(45deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px, linear-gradient(45deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 100%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed5: {
      background: `linear-gradient(45deg,transparent 15%, ${color} 15%, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 85%, transparent 85%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, transparent 15%, ${color} 15%, ${color} 20%, transparent 20%, transparent 80%, ${color} 80%, ${color} 85%, transparent 85%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top,transparent 9%, ${color} 10% ,${color} 15%, transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed6: {
      background: `linear-gradient(45deg,${color} 20%, transparent 20%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, transparent 80%, ${color} 80%) 0% 0% / ${size}px ${size}px, radial-gradient(${color} 20% , transparent 21%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed9: {
      background: `radial-gradient(circle at left center, transparent 14%, ${color} 15%, ${color} 25%, transparent 26%, transparent 59%, ${color} 60%, ${color} 70%, transparent 71%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right center, transparent 14%, ${color} 15%, ${color} 25%, transparent 26%, transparent 59%, ${color} 60%, ${color} 70%, transparent 71%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed10: {
      background: `radial-gradient(circle at top center, transparent 14%, ${color} 15%, ${color} 25%, transparent 26%, transparent 59%, ${color} 60%, ${color} 70%, transparent 71%) 0% 0% / ${size}px ${size}px,radial-gradient(circle at bottom center, transparent 14%, ${color} 15%, ${color} 25%, transparent 26%, transparent 59%, ${color} 60%, ${color} 70%, transparent 71%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed11: {
      background: `radial-gradient(circle at top left,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom left,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top right ,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom right,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle, transparent 25%, ${bgPageColor} 26%) 0% 0% / ${size}px ${size}px,linear-gradient(0deg, transparent 44%, ${color} 45%, ${color} 55%, transparent 56%) 0% 0% / ${size}px ${size}px, linear-gradient(90deg, transparent 44%, ${color} 45%, ${color} 55%, transparent 56%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed12: {
      background: `radial-gradient(circle at top left,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom left,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top right ,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom right,transparent 9%, ${color} 10% ,${color} 15% , transparent 16%) 0% 0% / ${size}px ${size}px, radial-gradient(circle, transparent 25%, ${bgPageColor} 26%) 0% 0% / ${size}px ${size}px,linear-gradient(45deg, transparent 46%, ${color} 47%, ${color} 52%, transparent 53%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, transparent 46%, ${color} 47%, ${color} 52%, transparent 53%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed13: {
      background: `radial-gradient(circle at top,transparent 14%, ${color} 15% ,${color} 19% , transparent 20%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom,transparent 14%, ${color} 15% ,${color} 19% , transparent 20%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at right,transparent 14%, ${color} 15% ,${color} 19% , transparent 20%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at left,transparent 14%, ${color} 15% ,${color} 19% , transparent 20%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at center,${color} 25% , transparent 26%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom left, ${color} 12%, transparent 13%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at bottom right, ${color} 12%, transparent 13%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top left, ${color} 12%, transparent 13%) 0% 0% / ${size}px ${size}px, radial-gradient(circle at top right, ${color} 12%, transparent 13%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
    Mixed14: {
      background: `linear-gradient(45deg , ${color} 10%,transparent 11%, transparent 89% , ${color} 90%) 0% 0% / ${size}px ${size}px,linear-gradient(135deg , ${color} 10%,transparent 11%, transparent 89% , ${color} 90%) 0% 0% / ${size}px ${size}px, radial-gradient(circle, transparent 25%, ${bgPageColor} 26%) 0% 0% / ${size}px ${size}px,linear-gradient(45deg, transparent 46%, ${color} 47%, ${color} 52%, transparent 53%) 0% 0% / ${size}px ${size}px, linear-gradient(135deg, transparent 46%, ${color} 47%, ${color} 52%, transparent 53%) 0% 0% / ${size}px ${size}px`,
      opacity,
    },
  }
}

export const BASE_STYLE = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
} as const

export const isYoutubeUrl = (url: string | undefined): boolean => {
  if (!url) return false
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/(shorts\/|watch\?v=|embed\/|v\/|.+)/
  return youtubeRegex.test(url)
}

export const getYoutubeVideoId = (url: string | undefined): string | null => {
  if (!url) return null
  const patterns = [
    /(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/shorts\/|\/user\/\S+|\/ytscreeningroom\?v=|\/sandalsResorts#\w\/\w\/.*\/))([^/&?]*)/,
    /youtube\.com\/embed\/([^/&?]*)/,
    /youtube-nocookie\.com\/embed\/([^/&?]*)/,
  ]

  for (const pattern of patterns) {
    const match = pattern.exec(url)
    if (match) return match[1]
  }
  return null
}

export const getYoutubeEmbedUrl = (url: string | undefined, loop: boolean | undefined, muted: boolean | undefined) => {
  if (!url) return ''
  const videoId = getYoutubeVideoId(url)
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${muted ? 1 : 0}&controls=0&loop=${loop ? 1 : 0}&playlist=${videoId}&modestbranding=1&playsinline=1&rel=0`
}

const hexToRgba = (hex: string, alpha: number = 1) => {
  if (!hex) return 'rgba(0, 0, 0, 0)'
  const argb = argbFromHex(hex)
  const r = redFromArgb(argb)
  const g = greenFromArgb(argb)
  const b = blueFromArgb(argb)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * @deprecated replaced by getBackgroundColorV2
 */
// const getBackgroundColor = (item: SectionBackgroundGroupProps, currentBrandkitTheme?: Theme, mode?: MtColorScheme) => {
//   if (item.values.mode === 'Gradient') {
//     const startOpacity = ((100 - ((item.values.gradientStartOpacity as number) ?? 0)) / 100).toFixed(2)
//     const endOpacity = ((100 - ((item.values.gradientEndOpacity as number) ?? 0)) / 100).toFixed(2)

//     const startValue =
//       currentBrandkitTheme && mode
//         ? getTokenColor((item.values.gradientStart as string) ?? 'primary-60', currentBrandkitTheme, mode)
//         : getCssVariableValue(`var(--color-${item.values.gradientStart ?? 'primary-60'})`)

//     if (!startValue) return

//     const endValue =
//       currentBrandkitTheme && mode
//         ? getTokenColor((item.values.gradientEnd as string) ?? 'secondary-60', currentBrandkitTheme, mode)
//         : getCssVariableValue(`var(--color-${item.values.gradientEnd ?? 'secondary-60'})`)

//     if (!endValue) return

//     const gradientStartColor = hexToRgba(startValue, parseFloat(startOpacity))
//     const gradientEndColor = hexToRgba(endValue, parseFloat(endOpacity))

//     return `linear-gradient(${item.values.direction ?? 'to bottom'},
//       ${gradientStartColor},
//       ${gradientEndColor})`
//   }
//   if (item.values.mode === 'Solid Color') {
//     return `var(--color-${item.values.color})`
//   }
//   return undefined
// }

const getBackgroundColorV2 = (item: SectionBackgroundGroupProps, mode: MtColorScheme) => {
  // Helper function to get mode value for current light/dark mode
  const getModeValue = (modeValue: SectionColorVariant | string | undefined): string => {
    if (!modeValue) return 'No Color'
    if (typeof modeValue === 'object' && mode in modeValue) {
      return modeValue[mode] as string
    }
    return modeValue as string
  }

  const currentMode = getModeValue((item.values as SectionBackgroundGroupValues).mode as any)

  if (currentMode === 'Gradient') {
    const startOpacity =
      typeof item.values.gradientStartOpacity === 'object'
        ? ((item.values.gradientStartOpacity as SectionColorVariant)[mode] as number)
        : 10
    const endOpacity =
      typeof item.values.gradientEndOpacity === 'object'
        ? ((item.values.gradientEndOpacity as SectionColorVariant)[mode] as number)
        : 10

    const gradientStart =
      typeof item.values.gradientStart === 'object'
        ? (item.values.gradientStart as SectionColorVariant)[mode]
        : item.values.gradientStart
    const gradientEnd =
      typeof item.values.gradientEnd === 'object'
        ? (item.values.gradientEnd as SectionColorVariant)[mode]
        : item.values.gradientEnd

    const startValue = getCssVariableValue(`var(--color-${gradientStart ?? 'primary-0'})`)

    if (!startValue) return

    const endValue = getCssVariableValue(`var(--color-${gradientEnd ?? 'primary-100'})`)

    if (!endValue) return

    const gradientStartColor = hexToRgba(startValue, startOpacity / 100)
    const gradientEndColor = hexToRgba(endValue, endOpacity / 100)

    return `linear-gradient(${item.values.direction ?? 'to bottom'}, 
      ${gradientStartColor},
      ${gradientEndColor})`
  }

  if (currentMode === 'Solid Color') {
    const colorOpacity =
      typeof item.values.colorOpacity === 'object'
        ? ((item.values.colorOpacity as SectionColorVariant)?.[mode] as number)
        : 100

    const color =
      typeof item.values.color === 'object' ? (item.values.color as SectionColorVariant)[mode] : item.values.color

    const colorValue = getCssVariableValue(`var(--color-${color ?? 'primary-0'})`)
    const finalColor = hexToRgba(colorValue, colorOpacity / 100)
    return finalColor
  }
  return undefined
}

export const sectionBackgroundGenerators = (
  videoSrc: string | undefined,
  imageSrc?: string | undefined,
  backgroundColorPage?: string,
  mode?: MtColorScheme
): any => {
  const bgPageColor = `var(--color-${backgroundColorPage})`

  const normalizeOpacity = (opacity: number | undefined) => {
    if (!opacity) return 0
    return opacity > 1 ? opacity / 100 : opacity
  }

  return {
    mask: (_item: SectionBackgroundGroupProps, zIndex: number) => ({
      ...BASE_STYLE,
      zIndex,
      color: 'currentColor', // Will inherit color from parent
    }),
    overlay: (item: SectionBackgroundGroupProps, zIndex: number) => ({
      ...BASE_STYLE,
      zIndex,
      backgroundColor: item.values.color,
      opacity: item.values.enabled ? normalizeOpacity(item.values.opacity as number) : 0,
    }),
    effect: (item: SectionBackgroundGroupProps, zIndex: number) => {
      const effectType = item.values.effectType || 'none'
      const effectOpacityValue = item.values.effectOpacity ?? 100
      const effectOpacity = typeof effectOpacityValue === 'number' ? effectOpacityValue : 100

      if (effectType === 'none') {
        return {
          ...BASE_STYLE,
          zIndex,
          display: 'none',
        }
      }

      // Base style for effect backgrounds
      // The actual rendering will be handled by EffectBackground component in layers.tsx
      return {
        ...BASE_STYLE,
        zIndex,
        opacity: normalizeOpacity(effectOpacity),
        position: 'relative',
        overflow: 'hidden',
      }
    },
    video: (_item: SectionBackgroundGroupProps, zIndex: number) => {
      const isYoutube = !!videoSrc && isYoutubeUrl(videoSrc)
      if (isYoutube) {
        return {
          ...BASE_STYLE,
          zIndex,
          overflow: 'hidden',
          '& iframe': {
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '100vw',
            height: '100vh',
            transform: 'translate(-50%, -50%) scale(1.5)',
            pointerEvents: 'none',
          },
        }
      }

      return {
        ...BASE_STYLE,
        zIndex,
        objectFit: 'cover',
      }
    },
    image: (item: SectionBackgroundGroupProps, zIndex: number) => {
      return {
        ...BASE_STYLE,
        zIndex,
        backgroundImage: imageSrc ? `url(${imageSrc})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: item.values.parallax ? 'fixed' : 'scroll',
        // transform: `translateY(${scrollPosition * 0.5}px)`, // Parallax effect
        transition: 'transform 0.1s ease-out',
      }
    },
    pattern: (item: SectionBackgroundGroupProps, zIndex: number) => {
      const color = `var(--color-${item.values.color ?? 'primary-10'})`
      const size = item.values.size ?? 24
      const opacity = item.values.opacity ?? 0.95
      const patternStyle = createPatternStyle(color, size as number, opacity as number, bgPageColor)[
        item.values.type?.toString() ?? 'Dots'
      ]
      return {
        ...BASE_STYLE,
        zIndex,
        backgroundColor: item.values.bg,
        ...patternStyle,
      }
    },
    color: (item: SectionBackgroundGroupProps, zIndex: number) => {
      return {
        ...BASE_STYLE,
        zIndex,
        background: getBackgroundColorV2(item, mode ?? 'light'),
      }
    },
  }
}
